import React, { useMemo, useEffect, useRef } from 'react';
import useReactRouter from 'use-react-router';
import window from 'window';
import qs from 'query-string';

import log from 'loglevel';

import { Switch, Route } from 'react-router-dom';
import Header from 'features/Header';

import Home from 'features/Home';

import ProductChoice from 'features/ProductChoice';
import CardChoice from 'features/CardChoice';
import ConsumerSearch from 'features/ConsumerSearch';
import EnhancedCheckoutConsumerSearch, {
  EnhancedCheckoutError,
} from 'features/enhancedCheckout/ConsumerSearch';
import CreditApplication from 'features/CreditApplication';
import MembershipValidation from 'features/MembershipValidation';
import LoyaltyNumberError from 'features/Home/LoyaltyNumberError';
import ClerkMode from 'features/ClerkMode';
import AddOns from 'features/AddOns';
import ReviewTerms from 'features/ReviewTerms';
import AppExpired from 'features/AppExpired';
import DspExpired from 'features/DspExpired';
import PrescreenOffer from 'features/prescreen/PrescreenOffer';
import BatchPrescreenStart from 'features/prescreen/BatchPrescreenStart';
import BatchPrescreenExpired from 'features/prescreen/BatchPrescreenStart/Expired';
import BatchPrescreenError from 'features/prescreen/BatchPrescreenStart/Error';
import FallbackReviewTerms from 'features/prescreen/FallbackReviewTerms';
import FallbackApplication from 'features/prescreen/FallbackApplication';
import PrequalFallbackApplication from 'features/prequal/FallbackApplication';
import PrequalConsumerSearch from 'features/prequal/ConsumerSearch';
import PrequalDecline from 'features/prequal/Decline';
import PrequalOffer from 'features/prequal/PrequalOffer';
import PrequalOfferPush from 'features/prequal/PrequalOfferPush';
import PrequalOfferError from 'features/prequal/PrequalOfferPush/Error';
import PrequalOfferExpired from 'features/prequal/PrequalOfferPush/Expired';
import PrequalOfferApplication, {
  PrequalOfferApplicationError,
  PrequalOfferApplicationPending,
  PrequalOfferApplicationNotQualified,
  PrequalOfferApplicationExistingPreapproval,
} from 'features/prequal/PrequalOfferApplication';
import PrequalOfferPushApplication from 'features/prequal/PrequalOfferPushApplication';
import PrequalFallbackReviewTerms from 'features/prequal/FallbackReviewTerms';
import DriverLicenseStart from 'features/DriverLicense/DriverLicenseStart';
import DriverLicenseHome from 'features/DriverLicense/DriverLicenseHome';
import DriverLicenseError from 'features/DriverLicense/DriverLicenseError';
import ScanPage from 'features/DriverLicense/ScanPage';
import PrequalDriverLicenseHome from 'features/prequal/PrequalDriverLicenseHome';

import CreditPending from 'features/CreditPending';
import ExistingAccount from 'features/ExistingAccount';
import ExistingAccountValidationError from 'features/ExistingAccount/ValidationError';
import CreditError from 'features/CreditError';
import DspStart from 'features/DspStart';
import DspError from 'features/DspError';
import DspRetrieval from 'features/DspRetrieval';
import ThemeProvider from 'features/Theme';
import SnackbarProvider from 'components/SnackbarProvider';

import AppTimeoutModal from 'features/TimeoutModal/AppTimeoutModal';
import DspTimeoutModal from 'features/TimeoutModal/DspTimeoutModal';
import ResetApplicationRoute from 'features/ResetApplicationRoute';
import WrappedRoute from 'routes/WrappedRoute';

import {
  EntryTokenProvider,
  EntryRoute,
  NonEntryRoute,
  NoReturnRoute,
} from 'routes/EntryProtectedRoute';

import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectTenant } from 'state/tenant';
import { IovationProvider } from 'hooks/useIovation';
import useFeature from 'hooks/useFeature';
import { TrackedRoute } from 'hooks/useAnalytics';
import useFmcHost, { FmcHostProvider } from 'hooks/useFmcHost';
import useFieldCustomization from 'hooks/useFieldCustomization';

import Redirect from 'components/Redirect';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import nest from 'utils/nest';
import UnloadPrompt from 'components/UnloadPrompt';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import { mergeApplication } from 'state/creditApplication';
import { loadAdobeTrackingId, heightChanged } from 'fmc-sdk';
import useHeightObserver from 'hooks/useHeightObserver';
import Approval from 'features/Approval';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import canUseDOM from 'utils/canUseDOM';
import ApplicationRoute from 'routes/ApplicationRoute';
import { ResponsiveQueryProvider } from 'hooks/useResponsiveQuery';

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const {
    stringsFileName,
    overrideConfig: { clerkMode },
  } = useSelector(selectTenant);

  const { isEnabled } = useFeature();
  const { hasStep } = useWorkflow();
  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();

  const { isRequired, isVisible } = useFieldCustomization();

  const { observeHeight } = useHeightObserver();

  const { setAnalyticsVariablesOnLoad } = useAnalyticsVariables();
  const { location } = useReactRouter();

  const { associateId, loyaltyNumber } = useMemo(() => {
    return qs.parse(location.search, {
      parseBooleans: true,
    });
  }, [location.search]);

  const hasClerkMode = useMemo(() => {
    return clerkMode && !associateId && isEnabled('clerkMode');
  }, [associateId, clerkMode, isEnabled]);

  const analyticsVariableSet = useRef<boolean>(false);
  if (!analyticsVariableSet.current && canUseDOM) {
    // We do this once on the first render on the client side to make sure it is
    // earlier than other tracking calls.
    setAnalyticsVariablesOnLoad();
    analyticsVariableSet.current = true;
  }

  useMemo(() => {
    i18n.setDefaultNamespace(stringsFileName || 'default');
  }, [i18n, stringsFileName]);

  useEffect(() => {
    if ('_satellite' in window) {
      let mcid: null | string = null;
      // We'll swallow all errors from the analytics suite and log it instead
      // to prevent it from breaking the app.
      try {
        mcid = window._satellite.getVisitorId().getMarketingCloudVisitorID();
      } catch (e) {
        log.error(e);
      }
      if (mcid) {
        fmcDispatch(loadAdobeTrackingId(mcid));
        dispatch(mergeApplication({ mcid }));
      }
    } else {
      log.warn('Adobe Satellite not loaded');
    }
  }, [dispatch, fmcDispatch, setAnalyticsVariablesOnLoad]);

  useEffect(() => {
    const onHeightChange = (height: number) => {
      fmcDispatch(heightChanged(height));
    };

    return observeHeight(document.body, onHeightChange);
  }, [observeHeight, fmcDispatch]);

  const hasLoyaltyNumberError = useMemo(() => {
    return (
      isRequired('loyaltyNumber') &&
      !isVisible('loyaltyNumber') &&
      !loyaltyNumber
    );
  }, [isRequired, isVisible, loyaltyNumber]);

  return (
    <div role="main">
      <Header />
      <ErrorBoundary>
        <Switch>
          <WrappedRoute
            path="/loyalty-number-error"
            wrappers={[TrackedRoute]}
            component={LoyaltyNumberError}
          />
          {hasLoyaltyNumberError && <Redirect to="/loyalty-number-error" />}

          {hasClerkMode && (
            <WrappedRoute
              exact
              path="/associate-details"
              wrappers={[EntryRoute, TrackedRoute]}
              component={ClerkMode}
            />
          )}

          {hasClerkMode && !canUseDOM && <Redirect to="/associate-details" />}

          {hasStep(Step.PRODUCT_CHOICE) ? (
            <WrappedRoute
              path="/product-choice"
              wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
              component={ProductChoice}
            />
          ) : null}

          {hasStep(Step.CARD_CHOICE) ? (
            <WrappedRoute
              path="/card-choice"
              wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
              component={CardChoice}
            />
          ) : null}

          {hasStep(Step.CONSUMER_SEARCH) ? (
            <WrappedRoute
              path="/consumer-search"
              wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
              component={ConsumerSearch}
            />
          ) : null}

          <WrappedRoute
            // This SHOULD NOT be an exact match because it renders
            // additional nested routes. If it's an exact match, then
            // /credit-application/<anything> won't be matched.
            path="/credit-application"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={CreditApplication}
          />

          {hasStep(Step.ADD_ONS) ? (
            <WrappedRoute
              path="/add-ons"
              wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
              component={AddOns}
            />
          ) : null}

          <WrappedRoute
            path="/review-terms"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={ReviewTerms}
          />

          {/** DSP PUSH */}

          {isEnabled('dspPush') ? (
            <WrappedRoute
              path="/dsp-start"
              wrappers={[TrackedRoute, EntryRoute, AppTimeoutModal]}
              component={DspStart}
            />
          ) : null}

          {isEnabled('dspRetrieval') ? (
            <WrappedRoute
              path="/dsp-retrieval"
              wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
              component={DspRetrieval}
            />
          ) : null}

          {/** PRESCREEN */}

          <WrappedRoute
            exact
            path="/prescreen/offer"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={PrescreenOffer}
          />

          <WrappedRoute
            exact
            path="/batch-prescreen/start"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={BatchPrescreenStart}
          />

          <WrappedRoute
            exact
            path="/batch-prescreen/expired"
            wrappers={[TrackedRoute, ApplicationRoute]}
            component={BatchPrescreenExpired}
          />

          <WrappedRoute
            exact
            path="/batch-prescreen/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={BatchPrescreenError}
          />

          <WrappedRoute
            path="/prescreen/application"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={FallbackApplication}
          />

          <WrappedRoute
            path="/prescreen/review-terms"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={FallbackReviewTerms}
          />

          {/* PREQUAL */}

          <WrappedRoute
            path="/prequal/phone-lookup"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={PrequalConsumerSearch}
          />

          <WrappedRoute
            exact
            path="/prequal/token-lookup"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={PrequalConsumerSearch}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplication}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplicationError}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application/pending"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplicationPending}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application/not-qualified"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplicationNotQualified}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application/existing-preapproval"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplicationExistingPreapproval}
          />

          <WrappedRoute
            path="/prequal/offer"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOffer}
          />

          <WrappedRoute
            path="/prequal/decline"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalDecline}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-push"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={PrequalOfferPush}
          />

          <WrappedRoute
            path="/prequal/offer-push-error"
            wrappers={[TrackedRoute, NonEntryRoute]}
            component={PrequalOfferError}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-push-expired"
            wrappers={[TrackedRoute, NoReturnRoute]}
            component={PrequalOfferExpired}
          />

          <WrappedRoute
            path="/prequal/offer-push-application"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferPushApplication}
          />

          <WrappedRoute
            path="/prequal/application"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalFallbackApplication}
          />

          <WrappedRoute
            path="/prequal/review-terms"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalFallbackReviewTerms}
          />

          {/** Enhanced checkout */}

          <WrappedRoute
            path="/enhanced-checkout/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={EnhancedCheckoutError}
          />

          <WrappedRoute
            path="/enhanced-checkout"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={EnhancedCheckoutConsumerSearch}
          />

          {/** Membership Validation */}

          <WrappedRoute
            exact
            path="/membership-validation"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={MembershipValidation}
          />

          {/** Driver License Scan Route **/}

          <WrappedRoute
            exact
            path="/driver-license"
            wrappers={[TrackedRoute, EntryRoute, AppTimeoutModal]}
            component={DriverLicenseHome}
          />

          <WrappedRoute
            exact
            path="/prequal/driver-license"
            wrappers={[TrackedRoute, EntryRoute, AppTimeoutModal]}
            component={PrequalDriverLicenseHome}
          />

          <WrappedRoute
            exact
            path="/driver-license/start"
            wrappers={[TrackedRoute, NonEntryRoute, AppTimeoutModal]}
            component={DriverLicenseStart}
          />

          <WrappedRoute
            exact
            path="/driver-license/scan-page"
            wrappers={[TrackedRoute, NonEntryRoute, AppTimeoutModal]}
            component={ScanPage}
          />

          <WrappedRoute
            exact
            path="/driver-license/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={DriverLicenseError}
          />

          {/** Ending routes */}

          <WrappedRoute
            path="/app-expired"
            wrappers={[TrackedRoute, ResetApplicationRoute, NoReturnRoute]}
            component={AppExpired}
          />

          <WrappedRoute
            path="/credit-pending"
            wrappers={[TrackedRoute, NonEntryRoute, ResetApplicationRoute]}
            component={CreditPending}
          />

          <WrappedRoute
            exact
            path="/existing-account/error"
            wrappers={[TrackedRoute, UnloadPrompt, NonEntryRoute]}
            component={ExistingAccountValidationError}
          />

          <WrappedRoute
            path="/existing-account"
            wrappers={[TrackedRoute, UnloadPrompt, NonEntryRoute]}
            component={ExistingAccount}
          />

          <WrappedRoute
            path="/credit-error"
            wrappers={[TrackedRoute, UnloadPrompt, NonEntryRoute]}
            component={CreditError}
          />

          <WrappedRoute
            path="/approval"
            wrappers={[
              TrackedRoute,
              UnloadPrompt,
              NonEntryRoute,
              DspTimeoutModal,
              ResetApplicationRoute,
            ]}
            component={Approval}
          />

          <WrappedRoute
            path="/dsp-expired"
            wrappers={[TrackedRoute, NonEntryRoute, UnloadPrompt]}
            component={DspExpired}
          />

          <WrappedRoute
            path="/dsp-error"
            wrappers={[TrackedRoute, UnloadPrompt, NonEntryRoute]}
            component={DspError}
          />

          {/** I had to move this to the bottom of the file, the reason for this is I had to remove the exact from the wrappedRoute as it renders multiple routes.
           * when the exact was removed this was matching all of the routes as it matches from top down.
           */}
          <WrappedRoute
            path="/"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={Home}
          />

          {process.env.NODE_ENV === 'development' ? (
            <Route exact path="/ssr-dev-template" component={CardChoice} />
          ) : null}

          <Route
            render={({ location }) => {
              log.info(`${location.pathname} not found.`);
              return <Redirect to="/" />;
            }}
          />
        </Switch>
      </ErrorBoundary>
    </div>
  );
};

const WithProviders = nest(
  ThemeProvider,
  SnackbarProvider,
  EntryTokenProvider,
  IovationProvider,
  FmcHostProvider,
  ResponsiveQueryProvider,
);

/**
 * The App component is nested because it depends on values provided
 * by the providers in its lifecycle.
 */
const AppWithProviders: React.FC = () => {
  return (
    <WithProviders>
      <App />
    </WithProviders>
  );
};

export default AppWithProviders;
